import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReceipt } from '@fortawesome/pro-regular-svg-icons/faReceipt';
import { faBox } from '@fortawesome/pro-regular-svg-icons/faBox';

import React, { useCallback } from 'react';

import { Divider } from 'ui-components/components/divider';
import { Text, TextType } from 'ui-components/components/text';

import { ReturnsIcon } from './returns-icon';
import { SidebarButton } from './sidebar-button';
import { SidePanel, useSidePanelContext } from '../side-panel';
import { SIDE_PANEL_CONTENT } from '../side-panel/constants';
import { useFulfillmentPlanIdContext } from '../../../fulfillment-plans/context/fulfillment-plan-id-context-provider';
import { useGetShipmentandReturnCount, getCountText } from './hooks';
import { useReturnIdContext } from '../context/return-id-context-provider';

import { messages } from './messages';
import style from './sidebar.module.less';

export const Sidebar = () => {
  const { toggleSidePanelWithContent, sidePanelContent } = useSidePanelContext();
  const sidePanelContentName = sidePanelContent?.name || SIDE_PANEL_CONTENT.NONE;

  const fulfillmentPlanId = useFulfillmentPlanIdContext();
  const returnId = useReturnIdContext();
  const { shipmentCount, returnCount } = useGetShipmentandReturnCount();

  const onClickOrderSummary = useCallback(() => {
    toggleSidePanelWithContent(SIDE_PANEL_CONTENT.ORDER_SUMMARY);
  }, [toggleSidePanelWithContent]);

  const onClickShipments = useCallback(() => {
    toggleSidePanelWithContent(SIDE_PANEL_CONTENT.SHIPMENTS);
  }, [toggleSidePanelWithContent]);

  const onClickReturns = useCallback(() => {
    toggleSidePanelWithContent(SIDE_PANEL_CONTENT.RETURNS);
  }, [toggleSidePanelWithContent]);

  const isSidePanelClosed = sidePanelContentName === SIDE_PANEL_CONTENT.NONE;
  const isOrderSummarySelected = sidePanelContentName === SIDE_PANEL_CONTENT.ORDER_SUMMARY;
  const isShipmentsSelected = sidePanelContentName === SIDE_PANEL_CONTENT.SHIPMENTS || (isSidePanelClosed && !!fulfillmentPlanId);
  const isReturnsSelected = sidePanelContentName === SIDE_PANEL_CONTENT.RETURNS || (isSidePanelClosed && !!returnId);
  const { shipmentCountInformation, returnCountInformation } = getCountText(shipmentCount, returnCount);

  return (
    <div className={style['order-details-sidebar-and-side-panel-container']}>
      <div className={style['order-details-sidebar']}>
        <SidebarButton
          isSelected={isOrderSummarySelected}
          onClick={onClickOrderSummary}
          tooltipMessageDescriptor={messages.orderSummaryButtonTooltip}
        >
          <FontAwesomeIcon className={style.icon} icon={faReceipt} />
        </SidebarButton>
        <Divider className={style.divider} orientation='horizontal' />
        <SidebarButton
          isSelected={isShipmentsSelected}
          onClick={onClickShipments}
          tooltipMessageDescriptor={messages.shipmentsButtonTooltip}
          tooltipMessageValues={{
            formatterCount: (chunks) => new Intl.NumberFormat().format(chunks),
            count: shipmentCount,
          }}
        >
          <FontAwesomeIcon className={style.icon} icon={faBox} />
          <Text formattedMessageValues={shipmentCountInformation} textType={TextType.Caption} value={messages.iconCount} />
        </SidebarButton>
        <SidebarButton
          isSelected={isReturnsSelected}
          onClick={onClickReturns}
          tooltipMessageDescriptor={messages.returnsButtonTooltip}
          tooltipMessageValues={{
            formatterCount: (chunks) => new Intl.NumberFormat().format(chunks),
            count: returnCount,
          }}
        >
          <ReturnsIcon className={style.icon} />
          <Text formattedMessageValues={returnCountInformation} textType={TextType.Caption} value={messages.iconCount} />
        </SidebarButton>
      </div>
      <SidePanel selectedViewId={sidePanelContentName} />
    </div>
  );
};
